import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ForcePasswordChange } from "@/components/ChangePassword";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { Layout } from "@/components/Layout";
import { canAccess, MODULES } from "@/lib/roles";
import { ModuleLocked } from "@/components/LicenceBanner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Production from "@/pages/Production";
import MasterData from "@/pages/MasterData";
import Dispatch from "@/pages/Dispatch";
import Manpower from "@/pages/Manpower";
import SalesMaster from "@/pages/SalesMaster";
import Meeting from "@/pages/Meeting";
import Trc from "@/pages/Trc";
import DailyPlan from "@/pages/DailyPlan";
import Ecn from "@/pages/Ecn";
import Approvals from "@/pages/Approvals";
import SecurityLog from "@/pages/SecurityLog";
import Scm from "@/pages/Scm";
import Sop from "@/pages/Sop";
import CustomerLoss from "@/pages/CustomerLoss";
import CustomerCare from "@/pages/CustomerCare";
import Quality from "@/pages/Quality";
import Qms from "@/pages/Qms";
import Npi from "@/pages/Npi";
import Ctb from "@/pages/Ctb";
import Setup from "@/pages/Setup";
import AdminSettings from "@/pages/AdminSettings";
import Onboard from "@/pages/Onboard";
import Vendor from "@/pages/Vendor";
import SetPassword from "@/pages/SetPassword";
import QuoteAccept from "@/pages/QuoteAccept";
import SupplierCapa from "@/pages/SupplierCapa";
import UploadHistory from "@/pages/UploadHistory";
import { ImportModeDialog } from "@/components/ImportModeDialog";
import CustomerPortal from "@/pages/CustomerPortal";
import Spares from "@/pages/Spares";
import ProductionLoss from "@/pages/ProductionLoss";
import Fives from "@/pages/Fives";
import Kaizen from "@/pages/Kaizen";
import Compliance from "@/pages/Compliance";
import Pnl from "@/pages/Pnl";
import Margins from "@/pages/Margins";
import CashFlow from "@/pages/CashFlow";
import Performance from "@/pages/Performance";
import Pipeline from "@/pages/Pipeline";
import Program from "@/pages/Program";
import WeeklyReview from "@/pages/WeeklyReview";
import Training from "@/pages/Training";
import ClosePoints from "@/pages/ClosePoints";
import Costing from "@/pages/Costing";

function Protected({ module, master, children }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-500 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.role === "super_admin") return <Navigate to="/vendor" replace />;
  if (settings.needs_setup && user.role === "master_admin" && !master) return <Navigate to="/setup" replace />;
  if (master && user.role !== "master_admin") return <Navigate to="/" replace />;
  if (module && !canAccess(user, module, settings.enabled_modules)) {
    if (canAccess(user, module) && Array.isArray(settings.enabled_modules)) return <Layout><ModuleLocked module={MODULES.find((m) => m.key === module)?.label || module} /></Layout>;
    const first = MODULES.find((m) => canAccess(user, m.key, settings.enabled_modules));
    if (!first) return <Layout><div className="pp-card p-6 text-sm text-slate-700" data-testid="no-modules-screen"><div className="font-bold mb-1">No modules assigned to your account</div>Ask your admin to grant View or Edit access to at least one module in Users → Permissions.</div></Layout>;
    if (module === "dashboard" || first.to === "/") return <Navigate to={first.to === "/" ? "/performance" : first.to} replace />;
    return <Navigate to={first.to} replace />;
  }
  return <Layout>{children}</Layout>;
}

function VendorOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-500 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/quote-accept" element={<QuoteAccept />} />
              <Route path="/close-points" element={<ClosePoints />} />
              <Route path="/supplier-capa" element={<SupplierCapa />} />
              <Route path="/portal/dispatch" element={<CustomerPortal />} />
              <Route path="/spares" element={<Protected module="spares"><Spares /></Protected>} />
              <Route path="/production-loss" element={<Protected module="prod_loss"><ProductionLoss /></Protected>} />
              <Route path="/5s" element={<Protected module="fives"><Fives /></Protected>} />
              <Route path="/kaizen" element={<Protected module="kaizen"><Kaizen /></Protected>} />
              <Route path="/pipeline" element={<Protected module="pipeline"><Pipeline /></Protected>} />
              <Route path="/program" element={<Protected module="program"><Program /></Protected>} />
              <Route path="/pnl" element={<Protected module="pnl"><Pnl /></Protected>} />
              <Route path="/margins" element={<Protected module="margins"><Margins /></Protected>} />
              <Route path="/cashflow" element={<Protected module="cashflow"><CashFlow /></Protected>} />
              <Route path="/performance" element={<Protected module="performance"><Performance /></Protected>} />
              <Route path="/weekly-review" element={<Protected module="meeting"><WeeklyReview /></Protected>} />
              <Route path="/training" element={<Protected><Training /></Protected>} />
              <Route path="/costing" element={<Protected module="costing"><Costing /></Protected>} />
              <Route path="/vendor" element={<VendorOnly><Vendor /></VendorOnly>} />
              <Route path="/setup" element={<Protected master><Setup /></Protected>} />
              <Route path="/admin" element={<Protected master><AdminSettings /></Protected>} />
              <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
              <Route path="/security-log" element={<Protected><SecurityLog /></Protected>} />
              <Route path="/scm" element={<Protected module="scm"><Scm /></Protected>} />
              <Route path="/onboard" element={<Protected module="master"><Onboard /></Protected>} />
              <Route path="/" element={<Protected module="dashboard"><Dashboard /></Protected>} />
              <Route path="/production" element={<Protected module="production"><Production /></Protected>} />
              <Route path="/entry" element={<Navigate to="/production" replace />} />
              <Route path="/sheet" element={<Navigate to="/production" replace />} />
              <Route path="/trc" element={<Protected module="trc"><Trc /></Protected>} />
              <Route path="/plan" element={<Protected module="plan"><DailyPlan /></Protected>} />
              <Route path="/ecn" element={<Protected module="ecn"><Ecn /></Protected>} />
              <Route path="/sop" element={<Protected module="sop"><Sop /></Protected>} />
              <Route path="/customer-loss" element={<Protected module="customer_loss"><CustomerLoss /></Protected>} />
              <Route path="/customer-care" element={<Protected module="customer_care"><CustomerCare /></Protected>} />
              <Route path="/quality" element={<Protected module="quality"><Quality /></Protected>} />
              <Route path="/qms" element={<Protected module="qms"><Qms /></Protected>} />
              <Route path="/npi" element={<Protected module="npi"><Npi /></Protected>} />
              <Route path="/compliance" element={<Protected module="compliance"><Compliance /></Protected>} />
              <Route path="/uploads" element={<Protected module="dashboard"><UploadHistory /></Protected>} />
              <Route path="/dispatch" element={<Protected module="dispatch"><Dispatch /></Protected>} />
              <Route path="/manpower" element={<Protected module="manpower"><Manpower /></Protected>} />
              <Route path="/sales" element={<Protected module="sales"><SalesMaster /></Protected>} />
              <Route path="/meeting" element={<Protected module="meeting"><Meeting /></Protected>} />
              <Route path="/ctb" element={<Protected module="ctb"><Ctb /></Protected>} />
              <Route path="/master" element={<Protected module="master"><MasterData /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster theme="light" position="top-right" richColors />
          <ImportModeDialog />
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
